/**
 * node-tooltip.tsx — Provenance tooltip for knowledge graph nodes.
 *
 * Shows source artifact, package, and an "Open artifact" link when a node
 * is hovered/focused on the /graph route. Keyboard-accessible: ESC dismisses.
 *
 * Positioning: absolute inside the graph container; caller supplies x/y in px.
 * Renders nothing when nodeId is null (tooltip hidden).
 */

import React, { useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGraphNodeProvenance } from './use-graph-node-provenance'

interface NodeTooltipProps {
  /** Currently hovered/focused node ID; null = hidden */
  nodeId: string | null
  /** Pixel X position relative to the graph container */
  x: number
  /** Pixel Y position relative to the graph container */
  y: number
  /** Called when ESC is pressed or tooltip requests close */
  onClose: () => void
}

/**
 * Floating provenance tooltip rendered over the graph canvas.
 * Keyboard-accessible: ESC closes, and the "Open artifact" link is focusable.
 */
export function NodeTooltip({ nodeId, x, y, onClose }: NodeTooltipProps) {
  const { t } = useTranslation('graph')
  const provenance = useGraphNodeProvenance(nodeId)

  /** Close on ESC */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!nodeId) return
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [nodeId, handleKeyDown])

  if (!nodeId || !provenance) return null

  const artifactHref = `/storage?artifact=${encodeURIComponent(nodeId)}`
  const sourceName = provenance.sourceFileName ?? t('nodeTooltip.unknown')
  const packageName = provenance.packageName ?? t('nodeTooltip.unknown')

  return (
    <div
      role="tooltip"
      data-testid="node-tooltip"
      aria-label={`Node provenance: ${provenance.label}`}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        zIndex: 50,
        pointerEvents: 'auto',
      }}
      className="min-w-[180px] max-w-[260px] rounded-lg border border-white/[0.08] bg-surface-1/95 px-3 py-2.5 shadow-xl backdrop-blur-sm"
    >
      {/* Node label */}
      <p className="mb-1.5 truncate text-xs font-semibold text-neutral-100">
        {provenance.label}
      </p>

      <dl className="flex flex-col gap-1">
        {/* Source */}
        <div className="flex items-baseline gap-1.5">
          <dt className="min-w-[52px] text-[10px] font-medium uppercase tracking-wider text-neutral-500">
            {t('nodeTooltip.source')}
          </dt>
          <dd
            className="truncate text-[11px] text-neutral-300"
            title={sourceName}
            data-testid="node-tooltip-source"
          >
            {sourceName}
          </dd>
        </div>

        {/* Package */}
        <div className="flex items-baseline gap-1.5">
          <dt className="min-w-[52px] text-[10px] font-medium uppercase tracking-wider text-neutral-500">
            {t('nodeTooltip.package')}
          </dt>
          <dd
            className="truncate text-[11px] text-neutral-300"
            title={packageName}
            data-testid="node-tooltip-package"
          >
            {packageName}
          </dd>
        </div>
      </dl>

      {/* Open artifact link */}
      <Link
        to={artifactHref}
        className="mt-2 block text-[11px] font-medium text-brand-400 hover:text-brand-300 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-400"
        data-testid="node-tooltip-open-artifact"
        aria-label={`${t('nodeTooltip.openArtifact')} ${provenance.label}`}
      >
        {t('nodeTooltip.openArtifact')}
      </Link>
    </div>
  )
}
